import React, { useState, useEffect } from 'react';
import {
    makeStyles, withStyles, Grid, TextField, MenuItem, Typography, 
    Zoom, Paper, Table, TableBody, TableCell, TableContainer, 
    TableHead, TableRow, Button, CircularProgress, Chip
} from '@material-ui/core';
import TimelineIcon from '@material-ui/icons/Timeline';
import ReportProblemIcon from '@material-ui/icons/ReportProblem';

const useStyles = makeStyles((theme) => ({
    root: { padding: theme.spacing(3) },
    headerSection: {
        marginBottom: theme.spacing(2),
        padding: theme.spacing(2),
        borderLeft: `5px solid ${theme.palette.primary.main}`,
        backgroundColor: theme.palette.background.paper,
    },
    tableContainer: {
        maxHeight: 400,
        borderRadius: 8,
        minHeight: 200,
        backgroundColor: theme.palette.background.paper,
    },
    analyzeBtn: {
        height: '100%',
        width: '100%',
        minHeight: '100px',
        fontWeight: 'bold',
    },
    logContainer: {
        marginTop: theme.spacing(4),
        padding: theme.spacing(2),
        backgroundColor: '#fafafa',
        borderRadius: 12,
        border: '1px solid #e0e0e0',
    },
    logHeader: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: theme.spacing(2),
        color: theme.palette.primary.dark,
    },
    logRow: {
        padding: theme.spacing(1.5),
        borderBottom: '1px solid #eee',
    }
}));

const StyledTableCell = withStyles((theme) => ({
    head: {
        backgroundColor: theme.palette.primary.dark,
        color: theme.palette.common.white,
        fontSize: 13,
        fontWeight: 'bold',
        textTransform: 'uppercase',
        zIndex: theme.zIndex.appBar - 1 
    },
    body: { fontSize: 13 },
}))(TableCell);

const AnomalyAnalysisComponent = () => {
    const classes = useStyles();
    
    // Estados de control
    const [selectedFrontera, setSelectedFrontera] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);

    const dataColumns = ['Frontera', 'Día/Hora', 'Valor Energía (kWh)', 'Est. Mínimo', 'Est. Máximo'];

    // Efecto para simular la carga de datos cuando cambia la frontera
    useEffect(() => {
        if (selectedFrontera) {
            setIsSearching(true);
            setHasSearched(false);
            
            const timer = setTimeout(() => {
                setIsSearching(false);
                setHasSearched(true);
            }, 1500); // 1.5 segundos de "búsqueda"

            return () => clearTimeout(timer);
        } else {
            setHasSearched(false);
            setIsSearching(false);
        }
    }, [selectedFrontera]);

    return (
        <div className={classes.root}>
            <Zoom in timeout={600}>
                <Grid container spacing={3}>
                    
                    {/* Header y Selector */}
                    <Grid item xs={12} className={classes.logContainer} component={Paper} elevation={1}>
                        <Grid container alignItems="center" spacing={2}>
                            <Grid item xs={12} md={6}>
                                <Typography variant="h6" color="primary">Detección de Desviación Significativa</Typography>
                                <Typography variant="caption" color="textSecondary">Monitoreo estadístico de fronteras</Typography>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Seleccionar Frontera"
                                    variant="outlined"
                                    size="small"
                                    value={selectedFrontera}
                                    onChange={(e) => setSelectedFrontera(e.target.value)}
                                >
                                    <MenuItem value=""><em>Ninguna</em></MenuItem>
                                    {["10000", "10001", "10002", "10003", "10004", "10005", "10006"].map((f) => (
                                        <MenuItem key={f} value={f}>{f}</MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                        </Grid>
                    </Grid>

                    {/* Tabla de Datos */}
                    <Grid item xs={12} md={10}>
                        <TableContainer component={Paper} className={classes.tableContainer} variant="outlined">
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        {dataColumns.map((col) => (
                                            <StyledTableCell key={col} align="center">{col}</StyledTableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    <TableRow>
                                        <TableCell colSpan={5} align="center" style={{ height: 180 }}>
                                            {isSearching ? (
                                                <CircularProgress size={30} />
                                            ) : hasSearched ? (
                                                <Typography color="primary" variant="body2" style={{ fontWeight: 'bold' }}>
                                                    No hay suficiente historia para el cliente seleccionado
                                                </Typography>
                                            ) : (
                                                // Si no hay frontera seleccionada, no se muestra texto
                                                null 
                                            )}
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Grid>

                    <Grid item xs={12} md={2}>
                        <Button 
                            variant="contained" 
                            disabled 
                            className={classes.analyzeBtn}
                            startIcon={<TimelineIcon style={{ fontSize: 40 }}/>}
                        >
                            Analizar
                        </Button>
                    </Grid>

                    {/* Bitácora (Estructura de cabecera) */}
                    <Grid item xs={12}>
                        <div className={classes.logContainer}>
                            <div className={classes.logHeader}>
                                <ReportProblemIcon color="primary" style={{ marginRight: 10 }} />
                                <Typography variant="h6">Bitácora de Eventos</Typography>
                            </div>
                            
                            <Grid container style={{ padding: '10px 15px', backgroundColor: '#eeeeee', borderRadius: '4px' }}>
                                <Grid item xs={2}><Typography variant="overline" style={{fontWeight: 'bold'}}>Fecha/Hora</Typography></Grid>
                                <Grid item xs={2}><Typography variant="overline" style={{fontWeight: 'bold'}}>Evento</Typography></Grid>
                                <Grid item xs={6}><Typography variant="overline" style={{fontWeight: 'bold'}}>Detalle</Typography></Grid>
                                <Grid item xs={2}><Typography variant="overline" style={{fontWeight: 'bold'}}>Estado</Typography></Grid>
                            </Grid>

                            <div className={classes.logRow} style={{ textAlign: 'center', padding: '20px' }}>
                                <Typography variant="caption" color="textSecondary">
                                    {selectedFrontera ? "No hay registro de eventos..." : "Seleccione una frontera para ver el historial de eventos"}
                                </Typography>
                            </div>
                        </div>
                    </Grid>

                </Grid>
            </Zoom>
        </div>
    );
};

export default AnomalyAnalysisComponent;